import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const flipperProjectsApi = createApi({
  reducerPath: "flipperProjectsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["FlipperProject", "FlipperProjectStats"],
  endpoints: (builder) => ({
    // Get all projects
    getAllProjects: builder.query({
      query: ({ stage, search, page = 1, limit = 100 } = {}) => {
        const params = new URLSearchParams();
        if (stage && stage !== "all") params.append("stage", stage);
        if (search) params.append("search", search);
        params.append("page", page);
        params.append("limit", limit);
        return `/flipper/projects?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.projects
          ? [
              ...result.projects.map(({ _id }) => ({ type: "FlipperProject", id: _id })),
              { type: "FlipperProject", id: "LIST" },
            ]
          : [{ type: "FlipperProject", id: "LIST" }],
    }),

    // Get project by ID
    getProjectById: builder.query({
      query: (id) => `/flipper/projects/${id}`,
      providesTags: (result, error, id) => [{ type: "FlipperProject", id }],
    }),

    // Create project
    createProject: builder.mutation({
      query: (projectData) => ({
        url: "/flipper/projects",
        method: "POST",
        body: projectData,
      }),
      invalidatesTags: [
        { type: "FlipperProject", id: "LIST" },
        { type: "FlipperProjectStats" },
      ],
    }),

    // Update project
    updateProject: builder.mutation({
      query: ({ id, ...projectData }) => ({
        url: `/flipper/projects/${id}`,
        method: "PUT",
        body: projectData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FlipperProject", id },
        { type: "FlipperProject", id: "LIST" },
        { type: "FlipperProjectStats" },
      ],
    }),

    // Delete project
    deleteProject: builder.mutation({
      query: (id) => ({
        url: `/flipper/projects/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "FlipperProject", id: "LIST" },
        { type: "FlipperProjectStats" },
      ],
    }),

    // Update project stage
    updateProjectStage: builder.mutation({
      query: ({ id, stage }) => ({
        url: `/flipper/projects/${id}/stage`,
        method: "PATCH",
        body: { stage },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FlipperProject", id },
        { type: "FlipperProject", id: "LIST" },
        { type: "FlipperProjectStats" },
      ],
    }),

    // Add timeline event
    addTimelineEvent: builder.mutation({
      query: ({ id, event, notes }) => ({
        url: `/flipper/projects/${id}/timeline`,
        method: "POST",
        body: { event, notes },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FlipperProject", id },
      ],
    }),

    // Get stats
    getProjectStats: builder.query({
      query: () => "/flipper/projects/stats",
      providesTags: [{ type: "FlipperProjectStats" }],
    }),
  }),
});

export const {
  useGetAllProjectsQuery,
  useGetProjectByIdQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectStageMutation,
  useAddTimelineEventMutation,
  useGetProjectStatsQuery,
} = flipperProjectsApi;
